import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/dropdown/dropdown.js";
import "@shoelace-style/shoelace/dist/components/menu/menu.js";
import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/divider/divider.js";
import { authService, type UserRole } from "../services/auth";
import { syncService, type SyncState } from "../services/sync";
import { Router } from "@vaadin/router";

@customElement("app-shell")
export class AppShell extends LitElement {
  @property({ type: String }) currentPath = "/";
  @property({ type: String }) pageTitle = "DAFNE Log";
  @state() private roles: UserRole[] = [];
  @state() private isAdmin = false;
  @state() private syncStatus: SyncState["status"] = "idle";
  @state() private lastSyncTime?: Date;
  @state() private pendingChanges = 0;
  private _syncUnsubscribe?: () => void;

  static styles = css`
    /* Box-sizing reset for shadow DOM */
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    :host {
      display: block;
      min-height: 100vh;
      overflow-x: hidden;
    }

    .app-container {
      display: flex;
      min-height: 100vh;
      max-width: 100%;
      overflow-x: hidden;
    }

    /* Side navigation - large screens only */
    .side-nav {
      display: none;
      flex-direction: column;
      position: fixed;
      left: 0;
      top: 0;
      bottom: 0;
      width: 72px;
      background: white;
      border-right: 1px solid #dee2e6;
      padding: 0.5rem 0;
      z-index: 1050;
    }

    .side-nav .logo {
      padding: 1rem;
      text-align: center;
      font-weight: 700;
      font-size: 1.25rem;
      color: #0d6efd;
      border-bottom: 1px solid #dee2e6;
      margin-bottom: 0.5rem;
    }

    .side-nav a {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 1rem 0.5rem;
      color: #6c757d;
      text-decoration: none;
      font-size: 0.625rem;
      text-align: center;
      transition: all 0.2s;
    }

    .side-nav a:hover {
      color: #0d6efd;
      background: #f8f9fa;
    }

    .side-nav a.active {
      color: #0d6efd;
      background: #e7f1ff;
    }

    .side-nav a sl-icon {
      font-size: 1.5rem;
      margin-bottom: 0.25rem;
    }

    /* Main content area */
    .main-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      width: 100vw;
      max-width: 100vw;
    }

    header {
      background-color: #0d6efd;
      color: white;
      padding: 0.75rem 1rem;
      position: sticky;
      top: 0;
      z-index: 1000;
      max-width: 100%;
    }

    header h1 {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .header-sync {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      transition: background 0.2s;
      font-size: 0.75rem;
    }

    .header-sync:hover {
      background: rgba(255, 255, 255, 0.15);
    }

    .header-sync sl-icon {
      font-size: 1.125rem;
    }

    .header-sync.syncing sl-icon {
      animation: spin 1s linear infinite;
    }

    .header-sync.offline {
      color: #ffc107;
    }

    .header-sync.error {
      color: #ff6b6b;
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    /* Side nav sync indicator */
    .side-nav .sync-status {
      margin-top: auto;
      padding: 1rem;
      border-top: 1px solid #dee2e6;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.75rem;
      color: #6c757d;
    }

    .side-nav .sync-status sl-icon {
      font-size: 1.25rem;
    }

    .side-nav .sync-status.syncing sl-icon {
      animation: spin 1s linear infinite;
      color: #0d6efd;
    }

    .side-nav .sync-status.offline sl-icon {
      color: #ffc107;
    }

    .side-nav .sync-status.error sl-icon {
      color: #dc3545;
    }

    .side-nav .sync-status .sync-btn {
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      transition: background 0.2s;
    }

    .side-nav .sync-status .sync-btn:hover {
      background: #e9ecef;
    }

    main {
      flex: 1;
      padding-bottom: 5rem;
    }

    /* Bottom navigation - phone/tablet */
    .bottom-nav {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: white;
      border-top: 1px solid #dee2e6;
      display: flex;
      justify-content: space-evenly;
      padding-bottom: env(safe-area-inset-bottom);
      z-index: 1050;
      height: 4rem;
    }

    .bottom-nav .nav-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 0.5rem 0.25rem;
      color: #6c757d;
      text-decoration: none;
      font-size: 0.625rem;
      flex: 1;
      max-width: 80px;
      transition: color 0.2s;
      cursor: pointer;
      background: none;
      border: none;
    }

    .bottom-nav .nav-item:hover {
      color: #0d6efd;
    }

    .bottom-nav .nav-item.active {
      color: #0d6efd;
    }

    .bottom-nav .nav-item sl-icon {
      font-size: 1.5rem;
      margin-bottom: 0.125rem;
    }

    /* More menu dropdown */
    .more-menu {
      --sl-panel-background-color: white;
    }

    .more-menu sl-menu {
      min-width: 180px;
    }

    .more-menu sl-menu-item::part(base) {
      font-size: 0.875rem;
    }

    .more-menu sl-menu-item sl-icon {
      font-size: 1.125rem;
    }

    /* Tablet layout - show both navs */
    @media (min-width: 768px) {
      header h1 {
        font-size: 1.5rem;
      }

      main {
        padding: 0 0.5rem;
      }
    }

    /* Large screen layout - side nav only */
    @media (min-width: 1024px) {
      .side-nav {
        display: flex;
      }

      .main-wrapper {
        margin-left: 72px;
        width: calc(100vw - 72px);
        max-width: calc(100vw - 72px);
      }

      header {
        display: none;
      }

      .bottom-nav {
        display: none;
      }

      main {
        padding-bottom: 1rem;
        padding-top: 1rem;
      }
    }

    /* Extra large screens */
    @media (min-width: 1200px) {
      .side-nav {
        width: 200px;
      }

      .side-nav a {
        flex-direction: row;
        justify-content: flex-start;
        padding: 0.875rem 1.5rem;
        font-size: 0.875rem;
      }

      .side-nav a sl-icon {
        margin-bottom: 0;
        margin-right: 0.75rem;
      }

      .main-wrapper {
        margin-left: 200px;
        width: calc(100vw - 200px);
        max-width: calc(100vw - 200px);
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.currentPath = window.location.pathname;
    window.addEventListener("vaadin-router-location-changed", () => {
      this.currentPath = window.location.pathname;
    });

    // Subscribe to auth state changes for role-based navigation
    const authState = authService.state;
    this.roles = authState.roles || [];
    this.isAdmin = authState.isAdmin || false;

    authService.subscribe((state) => {
      this.roles = state.roles || [];
      this.isAdmin = state.isAdmin || false;
    });

    // Subscribe to sync state changes
    this._syncUnsubscribe = syncService.subscribe((state) => {
      this.syncStatus = state.status;
      this.lastSyncTime = state.lastSyncTime;
      this.pendingChanges = state.pendingChanges;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._syncUnsubscribe?.();
  }

  private _formatSyncTime(): string {
    if (this.syncStatus === "syncing" || this.syncStatus === "checking") {
      return "Syncing...";
    }
    if (this.syncStatus === "offline") return "Offline";
    if (this.syncStatus === "error") return "Error";
    if (!this.lastSyncTime) return "Tap to sync";
    const now = new Date();
    const diff = Math.floor(
      (now.getTime() - this.lastSyncTime.getTime()) / 1000,
    );
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return this.lastSyncTime.toLocaleDateString();
  }

  private async _triggerSync() {
    if (this.syncStatus === "syncing" || this.syncStatus === "checking") return;
    await syncService.checkAndSync();
  }

  private _getSyncIcon(): string {
    switch (this.syncStatus) {
      case "syncing":
      case "checking":
        return "arrow-repeat";
      case "offline":
        return "wifi-off";
      case "error":
        return "exclamation-triangle";
      default:
        return this.pendingChanges > 0 ? "cloud-arrow-up" : "cloud-check";
    }
  }

  private _navigateTo(path: string) {
    Router.go(path);
  }

  private _isMoreActive(): boolean {
    const morePaths = [
      "/upload",
      "/help",
      "/share",
      "/sample",
      "/monitoring",
      "/admin",
    ];
    return morePaths.includes(this.currentPath);
  }

  // Helper methods for role checks
  private get isDiabetic(): boolean {
    return this.roles.includes("diabetic");
  }

  private get isMonitorOrClinician(): boolean {
    return this.roles.includes("monitor") || this.roles.includes("clinician");
  }

  render() {
    return html`
      <div class="app-container">
        <!-- Side navigation (large screens) -->
        <nav class="side-nav">
          <div class="logo">D</div>
          <a href="/" class=${this.currentPath === "/" ? "active" : ""}>
            <sl-icon name="calendar-day"></sl-icon>
            Today
          </a>
          <a
            href="/history"
            class=${this.currentPath === "/history" ? "active" : ""}
          >
            <sl-icon name="bar-chart"></sl-icon>
            History
          </a>
          <a
            href="/upload"
            class=${this.currentPath === "/upload" ? "active" : ""}
          >
            <sl-icon name="cloud-upload"></sl-icon>
            Import
          </a>
          <a
            href="/sample"
            class=${this.currentPath === "/sample" ? "active" : ""}
          >
            <sl-icon name="clipboard-data"></sl-icon>
            Sample
          </a>
          ${this.isDiabetic
            ? html`
                <a
                  href="/share"
                  class=${this.currentPath === "/share" ? "active" : ""}
                >
                  <sl-icon name="share"></sl-icon>
                  Share
                </a>
              `
            : ""}
          ${this.isMonitorOrClinician
            ? html`
                <a
                  href="/monitoring"
                  class=${this.currentPath === "/monitoring" ? "active" : ""}
                >
                  <sl-icon name="people"></sl-icon>
                  Monitoring
                </a>
              `
            : ""}
          ${this.isAdmin
            ? html`
                <a
                  href="/admin"
                  class=${this.currentPath === "/admin" ? "active" : ""}
                >
                  <sl-icon name="shield-lock"></sl-icon>
                  Admin
                </a>
              `
            : ""}
          <a href="/help" class=${this.currentPath === "/help" ? "active" : ""}>
            <sl-icon name="question-circle"></sl-icon>
            Help
          </a>
          <a
            href="/settings"
            class=${this.currentPath === "/settings" ? "active" : ""}
          >
            <sl-icon name="gear"></sl-icon>
            Settings
          </a>
          <!-- Sync status (side nav) -->
          <div
            class="sync-status ${this.syncStatus === "syncing" ||
            this.syncStatus === "checking"
              ? "syncing"
              : ""} ${this.syncStatus === "offline" ? "offline" : ""} ${this
              .syncStatus === "error"
              ? "error"
              : ""}"
            @click=${this._triggerSync}
          >
            <div class="sync-btn">
              <sl-icon name=${this._getSyncIcon()}></sl-icon>
            </div>
            <span>${this._formatSyncTime()}</span>
          </div>
        </nav>

        <div class="main-wrapper">
          <!-- Header (phone/tablet) -->
          <header>
            <div class="header-content">
              <h1>${this.pageTitle}</h1>
              <div
                class="header-sync ${this.syncStatus === "syncing" ||
                this.syncStatus === "checking"
                  ? "syncing"
                  : ""} ${this.syncStatus === "offline" ? "offline" : ""} ${this
                  .syncStatus === "error"
                  ? "error"
                  : ""}"
                @click=${this._triggerSync}
              >
                <sl-icon name=${this._getSyncIcon()}></sl-icon>
                <span>${this._formatSyncTime()}</span>
              </div>
            </div>
          </header>

          <main>
            <slot></slot>
          </main>

          <!-- Bottom navigation (phone/tablet) - 4 items only -->
          <nav class="bottom-nav">
            <a
              href="/"
              class="nav-item ${this.currentPath === "/" ? "active" : ""}"
            >
              <sl-icon name="calendar-day"></sl-icon>
              Today
            </a>
            <a
              href="/history"
              class="nav-item ${this.currentPath === "/history"
                ? "active"
                : ""}"
            >
              <sl-icon name="bar-chart"></sl-icon>
              History
            </a>
            <a
              href="/settings"
              class="nav-item ${this.currentPath === "/settings"
                ? "active"
                : ""}"
            >
              <sl-icon name="gear"></sl-icon>
              Settings
            </a>
            <sl-dropdown class="more-menu" placement="top-end" hoist>
              <button
                slot="trigger"
                class="nav-item ${this._isMoreActive() ? "active" : ""}"
              >
                <sl-icon name="three-dots"></sl-icon>
                More
              </button>
              <sl-menu
                @sl-select=${(e: CustomEvent) =>
                  this._navigateTo(e.detail.item.value)}
              >
                <sl-menu-item value="/upload">
                  <sl-icon slot="prefix" name="cloud-upload"></sl-icon>
                  Import CGM
                </sl-menu-item>
                <sl-menu-item value="/sample">
                  <sl-icon slot="prefix" name="clipboard-data"></sl-icon>
                  Sample Data
                </sl-menu-item>
                ${this.isDiabetic
                  ? html`
                      <sl-menu-item value="/share">
                        <sl-icon slot="prefix" name="share"></sl-icon>
                        Share Access
                      </sl-menu-item>
                    `
                  : ""}
                ${this.isMonitorOrClinician
                  ? html`
                      <sl-menu-item value="/monitoring">
                        <sl-icon slot="prefix" name="people"></sl-icon>
                        Monitoring
                      </sl-menu-item>
                    `
                  : ""}
                ${this.isAdmin
                  ? html`
                      <sl-divider></sl-divider>
                      <sl-menu-item value="/admin">
                        <sl-icon slot="prefix" name="shield-lock"></sl-icon>
                        Admin
                      </sl-menu-item>
                    `
                  : ""}
                <sl-divider></sl-divider>
                <sl-menu-item value="/help">
                  <sl-icon slot="prefix" name="question-circle"></sl-icon>
                  Help
                </sl-menu-item>
              </sl-menu>
            </sl-dropdown>
          </nav>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-shell": AppShell;
  }
}
